
import * as THREE from "three";
import * as MyUtils from "Utils/ViewerUtils";
import { saveBonesFile } from "services/WBCTService";
import ApiClient from "ApiClient";

export const BONES_X_SHIFT: number = 130;


export enum BoneName {
  DistalPhalanx_1 = 1,
  DistalPhalanx_2,
  DistalPhalanx_3,
  DistalPhalanx_4,
  DistalPhalanx_5,
  MiddlePhalanx_1,
  MiddlePhalanx_2,
  MiddlePhalanx_3,
  MiddlePhalanx_4,
  ProximalPhalanx_1,
  ProximalPhalanx_2,
  ProximalPhalanx_3,
  ProximalPhalanx_4,
  ProximalPhalanx_5,
  Metatarsal_1,
  Metatarsal_2,
  Metatarsal_3,
  Metatarsal_4,
  Metatarsal_5,
  Cuniform_Medial,
  Cuniform_Intermediate,
  Cuniform_Lateral,
  Cuboid,
  Navicular,
  Calcaneous,
  Talus,
  Tibia,
  Fibula,
  SesamoidL,
  SesamoidM,
}

// color association map
export const BoneColors: Record<BoneName, string> = {
  [BoneName.DistalPhalanx_1]: "BlueViolet",
  [BoneName.DistalPhalanx_2]: "Brown",
  [BoneName.DistalPhalanx_3]: "BurlyWood",
  [BoneName.DistalPhalanx_4]: "Chocolate",
  [BoneName.DistalPhalanx_5]: "Coral",
  [BoneName.MiddlePhalanx_1]: "CornflowerBlue",
  [BoneName.MiddlePhalanx_2]: "Crimson",
  [BoneName.MiddlePhalanx_3]: "DarkGoldenrod",
  [BoneName.MiddlePhalanx_4]: "DarkGreen",
  [BoneName.ProximalPhalanx_1]: "DarkMagenta",
  [BoneName.ProximalPhalanx_2]: "DarkOliveGreen",
  [BoneName.ProximalPhalanx_3]: "DarkOrange",
  [BoneName.ProximalPhalanx_4]: "DarkRed",
  [BoneName.ProximalPhalanx_5]: "DarkSlateBlue",
  [BoneName.Metatarsal_1]: "DodgerBlue",
  [BoneName.Metatarsal_2]: "Gold",
  [BoneName.Metatarsal_3]: "GreenYellow",
  [BoneName.Metatarsal_4]: "HotPink",
  [BoneName.Metatarsal_5]: "OrangeRed",
  [BoneName.Cuniform_Medial]: "Orchid",
  [BoneName.Cuniform_Intermediate]: "Teal",
  [BoneName.Cuniform_Lateral]: "Plum",
  [BoneName.Cuboid]: "Wheat",
  [BoneName.Navicular]: "Violet",
  [BoneName.Calcaneous]: "SteelBlue",
  [BoneName.Talus]: "CadetBlue",
  [BoneName.Tibia]: "Cornsilk",
  [BoneName.Fibula]: "SandyBrown",
  [BoneName.SesamoidL]: "DarkBlue",
  [BoneName.SesamoidM]: "LightBlue",
};


export async function saveCurrentInsoleBones(api: ApiClient, designId: number, file: any) {
  await saveBonesFile(api, designId, file);
}

export function getMarkerVertexPosition(
  mesh: THREE.Mesh,
  vertexIndex: number,
): THREE.Vector3 {
  const pos = mesh.geometry.attributes.position;
  const v = new THREE.Vector3(
    pos.getX(vertexIndex),
    pos.getY(vertexIndex),
    pos.getZ(vertexIndex)
  );

  return mesh.localToWorld(v.clone());
}

export function parseMarkersJson(raw: Record<string, string>, shift: THREE.Vector3 | null): Record<string, THREE.Vector3> {
  const output: Record<string, THREE.Vector3> = {};

  for (const key in raw) {
    if (!raw.hasOwnProperty(key)) continue;

    const numbers = raw[key]
      .split(",")
      .map(n => parseFloat(n.trim()));

    output[key] = shift ?
      new THREE.Vector3(numbers[0] + shift.x, numbers[1] + shift.y, numbers[2] + shift.z) :
      new THREE.Vector3(numbers[0], numbers[1], numbers[2]);
  }

  return output;
}

export function TranslateBonesToMainMesh(
  mesh: THREE.Mesh,
  bonesGroup: THREE.Group,
  foot: number
) {
  if (!mesh || !bonesGroup) return;

  // Ensure world matrices are updated
  mesh.updateMatrixWorld(true);
  bonesGroup.updateMatrixWorld(true);

  // Compute world-space bounding boxes
  const insoleBox = MyUtils.GetInsoleBox(mesh);
  const bonesBox = new THREE.Box3().setFromObject(bonesGroup);

  if (insoleBox.isEmpty() || bonesBox.isEmpty()) {
    console.warn("Cannot translate bones: empty bounding box.");
    return;
  }

  const liftInsoleShift = Math.pow(-1, foot + 1) * BONES_X_SHIFT;

  const insoleCenter = insoleBox.getCenter(new THREE.Vector3());
  const bonesCenter = bonesBox.getCenter(new THREE.Vector3());

  const shift = new THREE.Vector3(
    insoleCenter.x - bonesCenter.x + liftInsoleShift,
    insoleBox.min.y - bonesBox.min.y,
    insoleCenter.z - bonesCenter.z
  );

  // Apply shift ONCE to the entire group
  // bonesGroup.position.add(shift);
  // bonesGroup.updateMatrixWorld(true);

  return shift;
}

